/**
 * Focus selector component for rail items.
 * Renders the appropriate focus indicator based on the rail's display mode.
 * Supports different focus styles for photo, poster, character, square, and channel guide modes.
 */

import ChannelGuideHorizontalCardListFocus from '@components/atoms/focus/channelGuideHorizontalCardListFocus';
import CharacterCardListFocus from '@components/atoms/focus/characterCardListFocus';
import HorizontalCardListFocus from '../../../../atoms/focus/horizontalCardListFocus';
import VerticalCardListFocus from '../../../../atoms/focus/verticalCardListFocus';
import SquareCardListFocus from '../../../../atoms/focus/squareCardListFocus';
import { RailDisplayMode } from '../../../../../../shared/types/enums/rail';
import { magicRemoteSelector } from '../../../../../../shared/store/slices/magicRemote.slice';
import { useAppSelector } from '../../../../../../shared/store/hooks';

interface Props {
  display: boolean;
  displayMode: RailDisplayMode;
}

/**
 * Selects and renders the appropriate focus indicator based on display mode.
 * Returns null if magic remote is enabled or focus should not be displayed.
 *
 * @param {Props} props - Component props
 * @param {boolean} props.display - Whether to display the focus indicator
 * @param {RailDisplayMode} props.displayMode - The display mode of the rail
 * @returns {JSX.Element | null} Focus indicator component or null
 */
export default function FocusSelector({ display, displayMode }: Props) {
  const { enabled } = useAppSelector(magicRemoteSelector);

  if (enabled || !display) return null;

  switch (displayMode) {
    case RailDisplayMode.CHARACTER:
      return <CharacterCardListFocus />;
    case RailDisplayMode.POSTER:
      return <VerticalCardListFocus />;
    case RailDisplayMode.FILTER:
      return null;
    case RailDisplayMode.SQUARE:
      return <SquareCardListFocus />;
    case RailDisplayMode.CHANNEL_GUIDE_PHOTO:
      return <ChannelGuideHorizontalCardListFocus />;
    default:
      return displayMode ? <HorizontalCardListFocus /> : null;
  }
}
